/**
 * Procedural sound manager using the Web Audio API.
 * Synthesises all sounds at runtime — no audio files needed (M1).
 */

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let _muted = false;
let _volume = parseFloat(localStorage.getItem("mythglade_volume") ?? "0.6");

function getCtx(): AudioContext {
  if (!ctx) {
    ctx = new AudioContext();
    masterGain = ctx.createGain();
    masterGain.gain.value = _muted ? 0 : _volume;
    masterGain.connect(ctx.destination);
  }
  return ctx;
}

function getMaster(): GainNode {
  getCtx();
  return masterGain!;
}

/** Resume AudioContext after a user gesture (browser autoplay policy). */
export function resumeAudio(): void {
  const c = getCtx();
  if (c.state === "suspended") void c.resume();
}

export function setVolume(v: number): void {
  _volume = Math.max(0, Math.min(1, v));
  localStorage.setItem("mythglade_volume", String(_volume));
  if (masterGain && !_muted) masterGain.gain.value = _volume;
}

export function getVolume(): number {
  return _volume;
}

export function setMuted(m: boolean): void {
  _muted = m;
  localStorage.setItem("mythglade_muted", m ? "1" : "0");
  if (masterGain) masterGain.gain.value = m ? 0 : _volume;
}

export function isMuted(): boolean {
  return _muted;
}

// Restore mute state from localStorage
_muted = localStorage.getItem("mythglade_muted") === "1";

// ── Helpers ──────────────────────────────────────────────────────────

function gain(ac: AudioContext, vol: number): GainNode {
  const g = ac.createGain();
  g.gain.value = vol;
  g.connect(getMaster());
  return g;
}

function osc(
  ac: AudioContext,
  type: OscillatorType,
  freq: number,
  duration: number,
  dest: AudioNode,
  startOffset = 0,
): OscillatorNode {
  const o = ac.createOscillator();
  o.type = type;
  o.frequency.value = freq;
  o.connect(dest);
  const t = ac.currentTime + startOffset;
  o.start(t);
  o.stop(t + duration);
  return o;
}

function noise(ac: AudioContext, duration: number, vol: number, dest: AudioNode, startOffset = 0): AudioBufferSourceNode {
  const len = Math.ceil(ac.sampleRate * duration);
  const buf = ac.createBuffer(1, len, ac.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * vol;
  const src = ac.createBufferSource();
  src.buffer = buf;
  src.connect(dest);
  const t = ac.currentTime + startOffset;
  src.start(t);
  src.stop(t + duration);
  return src;
}

// ── Public sound effects ─────────────────────────────────────────────

let lastFootstep = 0;
const FOOTSTEP_INTERVAL_MS = 280;

/** Soft footstep — call every frame while moving; self-throttled. */
export function playFootstep(): void {
  const now = performance.now();
  if (now - lastFootstep < FOOTSTEP_INTERVAL_MS) return;
  lastFootstep = now;

  const ac = getCtx();
  const g = gain(ac, 0.08 + Math.random() * 0.04);
  const freq = 100 + Math.random() * 60;
  const dur = 0.06;
  const o = osc(ac, "triangle", freq, dur, g);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + dur);
  o.onended = () => g.disconnect();
}

/** Chat bar open / close pop. */
export function playChatToggle(): void {
  const ac = getCtx();
  const g = gain(ac, 0.12);
  const o = osc(ac, "sine", 660, 0.08, g);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.08);
  o.onended = () => g.disconnect();
}

/** Outgoing chat message. */
export function playChatSend(): void {
  const ac = getCtx();
  const g = gain(ac, 0.10);
  const o = osc(ac, "sine", 520, 0.12, g);
  o.frequency.exponentialRampToValueAtTime(780, ac.currentTime + 0.12);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.12);
  o.onended = () => g.disconnect();
}

/** Incoming chat message from another player. */
export function playChatReceive(): void {
  const ac = getCtx();
  const g = gain(ac, 0.08);
  osc(ac, "sine", 880, 0.06, g);
  const o2 = osc(ac, "sine", 1100, 0.06, g, 0.07);
  g.gain.setValueAtTime(0.08, ac.currentTime + 0.07);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.14);
  o2.onended = () => g.disconnect();
}

/** Generic UI click (sidebar buttons). */
export function playUIClick(): void {
  const ac = getCtx();
  const g = gain(ac, 0.10);
  const o = osc(ac, "sine", 600, 0.05, g);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.05);
  o.onended = () => g.disconnect();
}

/** Water splash for watering trees / extinguishing fire. */
export function playWaterSplash(): void {
  const ac = getCtx();
  // Filtered noise burst for water
  const g = gain(ac, 0.15);
  const filter = ac.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 2000;
  filter.Q.value = 0.5;
  filter.connect(g);
  const n = noise(ac, 0.35, 1, filter);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.35);
  n.onended = () => { filter.disconnect(); g.disconnect(); };
}

/** Ascending chime when a tree grows. */
export function playTreeGrow(): void {
  const ac = getCtx();
  const g = gain(ac, 0.10);
  const notes = [523, 659, 784]; // C5 E5 G5
  notes.forEach((freq, i) => {
    const dur = 0.15;
    const o = osc(ac, "sine", freq, dur, g, i * 0.12);
    if (i === notes.length - 1) {
      o.onended = () => g.disconnect();
    }
  });
  g.gain.setValueAtTime(0.10, ac.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.5);
}

/** Hiss when fire is extinguished. */
export function playFireExtinguish(): void {
  const ac = getCtx();
  const g = gain(ac, 0.12);
  const filter = ac.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.value = 3000;
  filter.connect(g);
  const n = noise(ac, 0.5, 1, filter);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.5);
  n.onended = () => { filter.disconnect(); g.disconnect(); };
}

/** Gentle ascending chime when a player joins the zone. */
export function playPlayerJoin(): void {
  const ac = getCtx();
  const g = gain(ac, 0.07);
  osc(ac, "sine", 440, 0.12, g);
  const o2 = osc(ac, "sine", 554, 0.12, g, 0.10);
  g.gain.setValueAtTime(0.07, ac.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.24);
  o2.onended = () => g.disconnect();
}

/** Soft descending tone when a player leaves the zone. */
export function playPlayerLeave(): void {
  const ac = getCtx();
  const g = gain(ac, 0.06);
  osc(ac, "sine", 554, 0.12, g);
  const o2 = osc(ac, "sine", 440, 0.12, g, 0.10);
  g.gain.setValueAtTime(0.06, ac.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.24);
  o2.onended = () => g.disconnect();
}

/** Emoji picker toggle pop. */
export function playEmojiPicker(): void {
  const ac = getCtx();
  const g = gain(ac, 0.08);
  const o = osc(ac, "sine", 900, 0.04, g);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.04);
  o.onended = () => g.disconnect();
}

/** New mail notification — bright ascending triple chime. */
export function playNewMail(): void {
  const ac = getCtx();
  const g = gain(ac, 0.12);
  const notes = [660, 880, 1100]; // E5 A5 C#6
  notes.forEach((freq, i) => {
    const dur = 0.1;
    const o2 = osc(ac, "sine", freq, dur, g, i * 0.09);
    if (i === notes.length - 1) {
      o2.onended = () => g.disconnect();
    }
  });
  g.gain.setValueAtTime(0.12, ac.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.4);
}

let lastBump = 0;
const BUMP_COOLDOWN_MS = 200;

/** Low thud when the player walks into a wall. Self-throttled. */
export function playBump(): void {
  const now = performance.now();
  if (now - lastBump < BUMP_COOLDOWN_MS) return;
  lastBump = now;

  const ac = getCtx();
  const g = gain(ac, 0.15);
  const dur = 0.1;
  const o1 = osc(ac, "triangle", 60 + Math.random() * 20, dur, g);
  // Quick noise burst for impact
  noise(ac, 0.04, 0.3, g);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + dur);
  o1.onended = () => g.disconnect();
}
